import { CommonModule } from "@angular/common";
import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxToggleComponent } from "@components/checkbox-side/checkbox-toggle.component";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { LinkComponent } from "@components/link/link.component";
import { SelectComponent } from "@components/select/select.component";
import { ColumnConfig, TableComponent } from "@components/table/table.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AssociateShopDto, EmployeeUpdateRequest } from "app/interfaces/employee.interface";
import { ApiService, UserInfo } from "app/services/api.service";

type RowAction = { 
        label?: string; 
        action: string; 
        iconSvg?: string; 
        visible?: (item: any) => boolean; 
};

@Component({
    selector: 'app-associate-shops',
    templateUrl: './associate-shops.component.html',
    styleUrl: './associate-shops.component.scss',
    imports: [CommonModule, TranslateModule, FormsModule, SelectComponent, ButtonComponent, CheckboxToggleComponent, LinkComponent, TableComponent, ConfirmModalComponent],
    standalone: true,
})

export class AssociateShopsComponent implements OnInit {
    userInfo: UserInfo;

    associateShops: AssociateShopDto[] = [];
    filteredShops: AssociateShopDto[] = [];
    originalAssociateShops: AssociateShopDto[] = [];
    hasChanges: boolean = false;

    rowActions: RowAction[] = [];
    shopColumns: ColumnConfig[] = [];

    isConfirmModalVisible: boolean = false;
    isSuccessModalVisible: boolean = false;
    contactToDelete: any = null;
    shopOfContact: AssociateShopDto | null = null;

    @Output() onSearch = new EventEmitter<string>();

    constructor(private api: ApiService, private translate: TranslateService, private router: Router) {}

    private translations(){
        this.translate.get([
            'my_profile.my_employees.employee_table.status',
            'my_profile.my_employees.employee_table.role',
            'my_profile.my_employees.employee_table.email',
            'my_profile.my_employees.employee_table.actions',
            'my_profile.my_employees.employee_table.access_we_professionals',
            'my_profile.my_employees.employee_form.access_e_cosentino',
            'my_profile.my_employees.employee_form.access_financial_portal',
            'my_profile.my_employees.employee_form.notifications_from_leads',
            'my_profile.my_employees.employee_form.nexus_access',
            'my_profile.canjear_puntos'
        ]).subscribe();
    }
    
    ngOnInit(): void {
        this.api.userInfo.pipe().subscribe((userInfo) => {
            this.userInfo = userInfo;

             const baseColumns: ColumnConfig[] = [
                { key: 'state', header: 'my_profile.my_employees.employee_table.status', format: 'text' },
                { key: 'rol', header: 'my_profile.my_employees.employee_table.role', format: 'text' },
                { key: 'email', header: 'my_profile.my_employees.employee_table.email', format: 'text' },
            ];

            const actionColumn: ColumnConfig = { key: 'actions', header: 'my_profile.my_employees.employee_table.actions', format: 'action' };

            const weColumn: ColumnConfig = { key: 'we_access', header: 'my_profile.my_employees.employee_table.access_we_professionals', format: 'toggle' };
            const ecosentinoColumn: ColumnConfig = { key: 'ecosentino_access', header: 'my_profile.my_employees.employee_form.access_e_cosentino', format: 'toggle' };
            const financialColumn: ColumnConfig = { key: 'financial_portal_access', header: 'my_profile.my_employees.employee_form.access_financial_portal', format: 'toggle' };
            const notificationsColumn: ColumnConfig = { key: 'we_clients_notifications', header: 'my_profile.my_employees.employee_form.notifications_from_leads', format: 'toggle' };
            const nexusColumn: ColumnConfig = { key: 'cnexus', header: 'my_profile.my_employees.employee_form.nexus_access', format: 'toggle' };
            const actiosColumn: ColumnConfig = { key: 'enabledGlobalRewards', header: 'my_profile.canjear_puntos', format: 'toggle' };

            switch (this.userInfo?.accountType) {
                case 'FABRICATOR':
                    this.shopColumns = [
                        ...baseColumns,
                        weColumn,
                        ecosentinoColumn,
                        financialColumn,
                        ...(this.userInfo?.isActiveLeadsB2C ? [notificationsColumn] : []),
                        ...(this.userInfo?.isActiveNexusProgram ? [nexusColumn] : []),
                        actiosColumn,
                        actionColumn,
                    ]
                    break;
                case 'INSTALLER':
                    this.shopColumns = [...baseColumns, weColumn, ecosentinoColumn, financialColumn, actionColumn];
                    break;
                case 'RETAIL':
                    this.shopColumns = [
                        ...baseColumns,
                        weColumn,
                        ...(this.userInfo?.isActiveLeadsB2C ? [notificationsColumn] : []),
                        ...(this.userInfo?.isActiveNexusProgram ? [nexusColumn] : []),
                        actiosColumn,
                        actionColumn,
                    ]
                    break;
                case 'ARCHITECT':
                case 'DESIGNER':
                    this.shopColumns = [...baseColumns, actionColumn];
                    break;
                default:
                    this.shopColumns = [...baseColumns, actionColumn];
                    break;
            }

            if (this.userInfo?.role === 'ADMIN') {
                this.rowActions = [
                    { action: 'edit', iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.625 20L10.625 18H21V20H8.625ZM4 19H5.075L14.975 9.1L13.9 8.025L4 17.925V19ZM17.1 8.375L14.6 5.9L15.825 4.675C16.025 4.475 16.2667 4.375 16.55 4.375C16.8333 4.375 17.0667 4.475 17.25 4.675L18.325 5.75C18.525 5.93333 18.625 6.16667 18.625 6.45C18.625 6.73333 18.5333 6.96667 18.35 7.15L17.1 8.375ZM16.4 9.1L5.5 20H3V17.5L13.9 6.6L16.4 9.1Z" fill="#365BCA"/></svg>` },
                    { action: 'delete', iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.625 20.0001C7.175 20.0001 6.79167 19.8418 6.475 19.5251C6.15833 19.2084 6 18.8251 6 18.3751V6.0001H5V5.0001H9V4.2251H15V5.0001H19V6.0001H18V18.3751C18 18.8418 17.846 19.2294 17.538 19.5381C17.2293 19.8461 16.8417 20.0001 16.375 20.0001H7.625ZM17 6.0001H7V18.3751C7 18.5584 7.05833 18.7084 7.175 18.8251C7.29167 18.9418 7.44167 19.0001 7.625 19.0001H16.375C16.5417 19.0001 16.6873 18.9374 16.812 18.8121C16.9373 18.6874 17 18.5418 17 18.3751V6.0001ZM9.8 17.0001H10.8V8.0001H9.8V17.0001ZM13.2 17.0001H14.2V8.0001H13.2V17.0001ZM7 6.0001V19.0001V18.3751V6.0001Z" fill="#365BCA"/></svg>` }
                ];
            } else {
                this.rowActions = [
                    {
                        action: 'edit',
                        iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.625 20L10.625 18H21V20H8.625ZM4 19H5.075L14.975 9.1L13.9 8.025L4 17.925V19ZM17.1 8.375L14.6 5.9L15.825 4.675C16.025 4.475 16.2667 4.375 16.55 4.375C16.8333 4.375 17.0667 4.475 17.25 4.675L18.325 5.75C18.525 5.93333 18.625 6.16667 18.625 6.45C18.625 6.73333 18.5333 6.96667 18.35 7.15L17.1 8.375ZM16.4 9.1L5.5 20H3V17.5L13.9 6.6L16.4 9.1Z" fill="#365BCA"/></svg>`,
                        visible: (item) => item.rol === 'User'
                    }
                ];
            }
        });

        this.api.getAssociateShops().subscribe({
            next: (shops: AssociateShopDto[]) => {
                this.associateShops = shops;
                this.filteredShops = shops;
                this.originalAssociateShops = JSON.parse(JSON.stringify(shops));
            },
            error: (err) => {
                console.error('Error al obtener tiendas asociadas', err);
            },
        });
    }

    formatContactsForTable(shop: AssociateShopDto) {
        return shop.contacts.map(c => ({
            ...c,
            state: `${c.firstName} ${c.lastName}`,
            rol: c.primary ? 'Admin' : 'User',
            we_access: c.active,
            enabledGlobalRewards: c.enabledGlobalRewards,
            ecosentino_access: c.enabledEcosentino,
            financial_portal_access: c.enabledFinancialPortal,
            we_clients_notifications: c.receiveLeads,
            actions: ''
        }));
    }

    onShopContactToggle(event: { item: any; key: string; value: boolean }, shop: AssociateShopDto) {
        const contact = shop.contacts.find((c) => c.id === event.item.id);
        if (!contact) return;

        switch (event.key) {
            case 'we_access': contact.active = event.value; break;
            case 'ecosentino_access': contact.enabledEcosentino = event.value; break;
            case 'financial_portal_access': contact.enabledFinancialPortal = event.value; break;
            case 'we_clients_notifications': contact.receiveLeads = event.value; break;
            case 'cnexus': contact.cnexus = event.value; break;
            case 'enabledGlobalRewards': contact.enabledGlobalRewards = event.value; break;
        }
        this.checkForChanges();
    }

    checkForChanges() {
        this.hasChanges = JSON.stringify(this.associateShops) !== JSON.stringify(this.originalAssociateShops);
    }

    onShopRowAction(event: { action: string; item: any }, shop: AssociateShopDto) {
        if (event.action === 'edit') {
            this.router.navigate(['/employee'], { state: { employee: event.item, shopId: shop.id } });
        } else if (event.action === 'delete' && this.userInfo?.role === 'ADMIN') {
            this.contactToDelete = event.item;
            this.shopOfContact = shop;
            this.isConfirmModalVisible = true;
        }
    }

    onConfirmDeleteShopContact() {
        if (!this.contactToDelete || !this.shopOfContact) return;

        this.api.deleteShopEmployee(this.shopOfContact.id, this.contactToDelete.id).subscribe(() => {
            this.shopOfContact!.contacts = this.shopOfContact!.contacts.filter(c => c.id !== this.contactToDelete.id);
            this.isConfirmModalVisible = false;
            this.isSuccessModalVisible = true;
        });
    }

    closeSuccessModal() {
        this.isSuccessModalVisible = false;
        this.contactToDelete = null;
        this.shopOfContact = null;
    }

    onAddEmployee(shop: AssociateShopDto) {
        this.router.navigate(['/employee'], { state: { shopId: shop.id } });
    }

    onChange(event: Event) {
        const value = (event.target as HTMLInputElement).value.toLowerCase().trim();

        if (!value) {
            this.filteredShops = this.associateShops;
            return;
        }

        this.filteredShops = this.associateShops.filter(shop =>
            shop.name.toLowerCase().includes(value)
        );
    }

    onSaveAll() {
        const updates: { shopId: string; employees: EmployeeUpdateRequest[] }[] = [];

        this.associateShops.forEach((shop, shopIndex) => {
            const originalShop = this.originalAssociateShops[shopIndex];
            if (!originalShop) return;

            const shopUpdates: EmployeeUpdateRequest[] = shop.contacts
            .filter((c, contactIndex) => {
                const originalContact = originalShop.contacts[contactIndex];
                if (!originalContact) return true;

                return (
                    c.firstName !== originalContact.firstName ||
                    c.lastName !== originalContact.lastName ||
                    c.email !== originalContact.email ||
                    c.phone !== originalContact.phone ||
                    c.contactType !== originalContact.contactType ||
                    c.primary !== originalContact.primary ||
                    c.active !== originalContact.active ||
                    c.enabledEcosentino !== originalContact.enabledEcosentino ||
                    c.enabledFinancialPortal !== originalContact.enabledFinancialPortal ||
                    c.receiveLeads !== originalContact.receiveLeads ||
                    c.cnexus !== originalContact.cnexus ||
                    c.enabledGlobalRewards !== originalContact.enabledGlobalRewards
                );
            })
            .map((c) => ({
                id: c.id,
                firstName: c.firstName,
                lastName: c.lastName,
                email: c.email,
                phone: c.phone,
                contactType: c.contactType,
                primary: c.primary,
                active: c.active,
                enabledEcosentino: c.enabledEcosentino,
                enabledFinancialPortal: c.enabledFinancialPortal,
                receiveLeads: c.receiveLeads,
                cnexus: c.cnexus,
                enabledGlobalRewards: c.enabledGlobalRewards
            }));

            if (shopUpdates.length > 0) {
                updates.push({ shopId: shop.id, employees: shopUpdates });
            }
        });

        if (updates.length === 0) {
            console.log('No hay cambios que guardar');
            return;
        }

        updates.forEach((u) => {
            this.api.updateShopEmployees(u.shopId, u.employees).subscribe({
            next: (updated) => {
                console.log(`Contactos actualizados para tienda ${u.shopId}`, updated);

                const shop = this.associateShops.find(s => s.id === u.shopId);
                if (shop) {
                shop.contacts = updated.map((e: any) => ({
                    ...e,
                    state: `${e.firstName} ${e.lastName}`,
                    rol: e.primary ? 'Admin' : 'User',
                    we_access: e.active,
                    ecosentino_access: e.enabledEcosentino,
                    financial_portal_access: e.enabledFinancialPortal,
                    we_clients_notifications: e.receiveLeads,
                    actions: ''
                }));
                }

                this.originalAssociateShops = JSON.parse(JSON.stringify(this.associateShops));
                this.hasChanges = false;
            },
            error: (err) => console.error(`Error al actualizar contactos de tienda ${u.shopId}`, err)
            });
        });
    }
}